"use client";

import { useCallback, useState } from "react";

const SKILL_MONO_FONT =
  'ui-monospace, "SFMono-Regular", "SF Mono", Menlo, Consolas, "Liberation Mono", monospace';

function HumanIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      viewBox="0 0 24 24"
    >
      <path
        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function AgentIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      viewBox="0 0 24 24"
    >
      <rect height="14" rx="3" width="18" x="3" y="6" />
      <path
        d="M8 11h.01M16 11h.01M9 16h6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M12 2v4" strokeLinecap="round" />
    </svg>
  );
}

function TabButton({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 font-semibold text-[0.85rem] transition-all ${
        active
          ? "bg-[var(--explore-bg-card)] text-[var(--explore-text-primary)] shadow-[0_1px_3px_rgba(0,0,0,0.06)]"
          : "text-[var(--explore-text-muted)] hover:text-[var(--explore-text-secondary)]"
      }`}
      onClick={onClick}
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
      type="button"
    >
      {children}
    </button>
  );
}

function StepNumber({ children }: { children: React.ReactNode }) {
  return (
    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--explore-bg-sidebar)] font-semibold text-[0.75rem] text-[var(--explore-text-primary)]">
      {children}
    </span>
  );
}

function CommandBlock({ command }: { command: string }) {
  return (
    <div className="rounded-xl border border-[var(--explore-border-light)] bg-[var(--explore-bg-sidebar)] px-3 py-2.5">
      <code
        className="block overflow-x-auto whitespace-pre-wrap break-all text-[0.82rem] text-[var(--explore-text-primary)] leading-relaxed"
        style={{ fontFamily: SKILL_MONO_FONT }}
      >
        {command}
      </code>
    </div>
  );
}

function CopyInstallButton({
  copied,
  onCopy,
}: {
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <button
      className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[var(--explore-accent-yellow)] px-5 py-3.5 font-bold text-[0.92rem] text-[var(--explore-on-accent-text)] transition-opacity hover:opacity-90"
      onClick={onCopy}
      type="button"
    >
      {copied ? (
        <>
          <svg
            aria-hidden="true"
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.4}
            viewBox="0 0 24 24"
          >
            <path
              d="M5 13l4 4L19 7"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Copied!
        </>
      ) : (
        "Copy install command"
      )}
    </button>
  );
}

export function SkillInstallSidebar({
  humanInstallCommand,
  repoUrl,
  slug,
}: {
  humanInstallCommand: string;
  repoUrl: string;
  slug: string;
}) {
  const [installTab, setInstallTab] = useState<"human" | "agent">("human");
  const [copied, setCopied] = useState(false);
  const agentInstruction = `Install the skill from ${repoUrl}`;
  const displayedCommand =
    installTab === "human" ? humanInstallCommand : agentInstruction;

  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(displayedCommand);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }, [displayedCommand]);

  return (
    <div
      className="rounded-3xl border-2 border-[var(--explore-accent-yellow)] bg-[var(--explore-bg-card)] p-6"
      style={{ boxShadow: "var(--explore-card-shadow-lg)" }}
    >
      <h3
        className="mb-4 font-bold text-[1.6rem] text-[var(--explore-text-primary)]"
        style={{ fontFamily: SKILL_MONO_FONT }}
      >
        Install
      </h3>

      <div className="mb-5 flex gap-1 rounded-2xl bg-[var(--explore-bg-sidebar)] p-1">
        <TabButton
          active={installTab === "human"}
          onClick={() => {
            setInstallTab("human");
            setCopied(false);
          }}
        >
          <HumanIcon />
          I&apos;m a Human
        </TabButton>
        <TabButton
          active={installTab === "agent"}
          onClick={() => {
            setInstallTab("agent");
            setCopied(false);
          }}
        >
          <AgentIcon />
          I&apos;m an Agent
        </TabButton>
      </div>

      {installTab === "agent" ? (
        <p className="mb-3 text-[0.85rem] text-[var(--explore-text-secondary)] leading-relaxed">
          Paste this into your agent session — Claude (or Cursor, Codex, etc.)
          will understand and install the skill itself.
        </p>
      ) : null}

      <CommandBlock command={displayedCommand} />
      <CopyInstallButton copied={copied} onCopy={handleCopy} />

      {installTab === "human" ? (
        <details className="group mt-3">
          <summary className="flex cursor-pointer list-none items-center justify-center gap-1.5 rounded-xl border border-[var(--explore-border-light)] bg-transparent px-3 py-2 text-[0.82rem] text-[var(--explore-text-secondary)] transition-colors hover:bg-[var(--explore-bg-sidebar)] hover:text-[var(--explore-text-primary)]">
            <svg
              aria-hidden="true"
              className="h-3.5 w-3.5"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              viewBox="0 0 24 24"
            >
              <path
                d="M12 9v2m0 4h.01M12 2a10 10 0 100 20 10 10 0 000-20z"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="group-open:hidden">Show step-by-step</span>
            <span className="hidden group-open:inline">Hide step-by-step</span>
            <svg
              aria-hidden="true"
              className="h-3 w-3 transition-transform group-open:rotate-180"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path
                d="M6 9l6 6 6-6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </summary>

          <ol className="mt-4 space-y-5">
            <li className="flex gap-3">
              <StepNumber>1</StepNumber>
              <div className="min-w-0 flex-1 pt-0.5">
                <p className="font-semibold text-[0.9rem] text-[var(--explore-text-primary)]">
                  Open your terminal
                </p>
                <ul className="mt-2 space-y-1.5 text-[0.8rem] text-[var(--explore-text-secondary)] leading-relaxed">
                  <li className="flex items-start gap-1.5">
                    <span aria-hidden="true">🍎</span>
                    <span>
                      <span className="font-medium text-[var(--explore-text-primary)]">
                        Mac:
                      </span>{" "}
                      Press{" "}
                      <kbd className="rounded border border-[var(--explore-border-light)] bg-[var(--explore-bg-sidebar)] px-1.5 py-0.5 font-mono text-[0.7rem] text-[var(--explore-text-primary)]">
                        ⌘ Space
                      </kbd>
                      , type "Terminal", press Enter
                    </span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <span aria-hidden="true">🪟</span>
                    <span>
                      <span className="font-medium text-[var(--explore-text-primary)]">
                        Windows:
                      </span>{" "}
                      Press{" "}
                      <kbd className="rounded border border-[var(--explore-border-light)] bg-[var(--explore-bg-sidebar)] px-1.5 py-0.5 font-mono text-[0.7rem] text-[var(--explore-text-primary)]">
                        Win R
                      </kbd>
                      , type "cmd", press Enter
                    </span>
                  </li>
                </ul>
              </div>
            </li>

            <li className="flex gap-3">
              <StepNumber>2</StepNumber>
              <div className="min-w-0 flex-1 pt-0.5">
                <p className="font-semibold text-[0.9rem] text-[var(--explore-text-primary)]">
                  Paste the command above and press Enter
                </p>
                <p className="mt-1 text-[0.8rem] text-[var(--explore-text-secondary)] leading-relaxed">
                  Use the Copy command button, then paste in your terminal (Mac:
                  ⌘V, Windows: Ctrl V).
                </p>
              </div>
            </li>

            <li className="flex gap-3">
              <StepNumber>3</StepNumber>
              <div className="min-w-0 flex-1 pt-0.5">
                <p className="font-semibold text-[0.9rem] text-[var(--explore-text-primary)]">
                  Restart Claude Code
                </p>
                <p className="mt-1 text-[0.8rem] text-[var(--explore-text-secondary)] leading-relaxed">
                  Close and reopen Claude Code, or start a new session, so it
                  picks up the new skill.
                </p>
              </div>
            </li>
          </ol>
        </details>
      ) : null}

      <div className="mt-6 border-[var(--explore-border-light)] border-t pt-5">
        <details className="group">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-2 text-[0.82rem] text-[var(--explore-text-secondary)] hover:text-[var(--explore-text-primary)]">
            <span className="inline-flex items-center gap-2">
              <svg
                aria-hidden="true"
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
                viewBox="0 0 24 24"
              >
                <path
                  d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Where it lives
            </span>
            <svg
              aria-hidden="true"
              className="h-3.5 w-3.5 transition-transform group-open:rotate-180"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path
                d="M6 9l6 6 6-6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </summary>
          <div className="mt-3 rounded-xl border border-[var(--explore-border-light)] bg-[var(--explore-bg-sidebar)] px-3 py-2.5">
            <code
              className="block text-[0.78rem] text-[var(--explore-text-primary)] leading-relaxed"
              style={{ fontFamily: SKILL_MONO_FONT }}
            >
              ~/.claude/skills/{slug}/
              <br />
              ├── SKILL.md
              <br />
              └── ... (skill resource files)
            </code>
          </div>
        </details>

        <a
          className="mt-3 flex items-center gap-2 rounded-2xl border border-[var(--explore-border-light)] bg-[var(--explore-bg-card)] px-4 py-3 text-[0.9rem] text-[var(--explore-text-secondary)] no-underline transition-all hover:border-[var(--explore-text-muted)] hover:text-[var(--explore-text-primary)]"
          href={repoUrl}
          rel="noopener noreferrer"
          style={{ fontFamily: SKILL_MONO_FONT }}
          target="_blank"
        >
          <svg
            aria-hidden="true"
            className="h-5 w-5"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z" />
          </svg>
          View on GitHub
          <svg
            aria-hidden="true"
            className="ml-auto h-3.5 w-3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path
              d="M7 17L17 7M17 7H7M17 7v10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </a>
      </div>
    </div>
  );
}
